'use client';

import { motion } from "framer-motion";
import Link from "next/link";
import { ArrowLeft, BarChart3, PieChart, TrendingUp, CheckCircle, Code, Eye, Layers, Activity, Users, Target, Zap } from "lucide-react";
import { useParams } from "next/navigation";
import { localizeSpelling } from "@/lib/spelling-localization";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const dataVisualizationData = {
  title: "Data Visualization",
  subtitle: "Transform complex data into compelling visual stories that drive insights, decision-making, and business growth through interactive dashboards and custom visualizations",
  headerGradient: "bg-gradient-to-r from-emerald-600 via-teal-500 to-cyan-500",
  accentColor: "text-emerald-600",
  buttonColor: "bg-emerald-600 hover:bg-emerald-700",
  
  overview: "Data Visualization transforms raw data into meaningful visual representations that enable stakeholders to understand complex information quickly and make informed decisions. Our data visualization solutions combine advanced analytics with intuitive design to create interactive dashboards, real-time reporting systems, and custom visualizations that reveal hidden patterns and drive business intelligence.",
  
  capabilities: [
    {
      name: "Interactive Dashboards",
      description: "Create dynamic, real-time dashboards that allow users to explore data interactively",
      icon: BarChart3,
      applications: ["Executive Dashboards", "KPI Monitoring", "Performance Tracking", "Business Intelligence"],
      useCases: ["Real-time metrics", "Drill-down analysis", "Custom filters", "Multi-source integration"]
    },
    {
      name: "Real-time Reporting",
      description: "Build automated reporting systems that update in real-time with live data",
      icon: Activity,
      applications: ["Live Analytics", "Operational Reports", "Alert Systems", "Monitoring Dashboards"],
      useCases: ["Instant updates", "Automated alerts", "Live streaming", "Real-time insights"]
    },
    {
      name: "Custom Visualizations",
      description: "Design bespoke visual representations tailored to specific business needs",
      icon: Eye,
      applications: ["Industry-specific Charts", "Geographic Maps", "Network Diagrams", "Custom Widgets"],
      useCases: ["Unique requirements", "Brand consistency", "Specialized data", "Complex relationships"]
    }
  ],
  
  technologies: [
    {
      category: "Visualization Libraries",
      tools: ["D3.js", "Chart.js", "Plotly", "Highcharts", "Recharts", "Victory"],
      description: "Powerful JavaScript libraries for creating interactive visualizations"
    },
    {
      category: "Business Intelligence",
      tools: ["Tableau", "Power BI", "Looker", "QlikView", "Sisense", "Grafana"],
      description: "Enterprise-grade BI platforms for comprehensive analytics"
    },
    {
      category: "Data Processing",
      tools: ["Python", "R", "Pandas", "NumPy", "Apache Spark", "Elasticsearch"],
      description: "Data processing and analysis tools for visualization preparation"
    },
    {
      category: "Web Frameworks",
      tools: ["React", "Vue.js", "Angular", "Svelte", "Observable", "Streamlit"],
      description: "Modern web frameworks for building interactive visualization applications"
    },
    {
      category: "Database & APIs",
      tools: ["PostgreSQL", "MongoDB", "InfluxDB", "GraphQL", "REST APIs", "WebSockets"],
      description: "Data storage and real-time communication technologies"
    },
    {
      category: "Cloud Platforms",
      tools: ["AWS QuickSight", "Google Data Studio", "Azure Power BI", "Databricks", "Snowflake", "BigQuery"],
      description: "Cloud-based visualization and analytics platforms"
    }
  ],
  
  industries: [
    {
      name: "Finance & Banking",
      applications: ["Risk dashboards", "Trading analytics", "Compliance reporting", "Customer insights"],
      impact: "60% faster decision-making, 40% improvement in risk detection"
    },
    {
      name: "Healthcare",
      applications: ["Patient monitoring", "Clinical analytics", "Population health", "Research visualization"],
      impact: "50% reduction in analysis time, improved patient outcomes"
    },
    {
      name: "E-commerce",
      applications: ["Sales analytics", "Customer behavior", "Inventory tracking", "Marketing ROI"],
      impact: "35% increase in conversion rates, 25% boost in sales"
    },
    {
      name: "Manufacturing",
      applications: ["Production monitoring", "Quality control", "Supply chain", "Equipment performance"],
      impact: "30% reduction in downtime, 20% improvement in efficiency"
    },
    {
      name: "Marketing & Advertising",
      applications: ["Campaign performance", "Audience analytics", "Attribution modeling", "Social media insights"],
      impact: "45% improvement in campaign ROI, better targeting accuracy"
    },
    {
      name: "Education",
      applications: ["Student performance", "Learning analytics", "Institutional metrics", "Research data"],
      impact: "Enhanced learning outcomes, data-driven educational decisions"
    }
  ],
  
  visualizationTypes: [
    {
      type: "Statistical Charts",
      description: "Traditional charts for quantitative data analysis",
      examples: ["Bar Charts", "Line Graphs", "Scatter Plots", "Histograms", "Box Plots", "Heat Maps"],
      useCases: ["Trend analysis", "Comparisons", "Distributions", "Correlations"]
    },
    {
      type: "Geographic Visualizations",
      description: "Location-based data representations",
      examples: ["Choropleth Maps", "Point Maps", "Flow Maps", "3D Terrain", "Satellite Imagery", "GPS Tracking"],
      useCases: ["Regional analysis", "Location intelligence", "Route optimization", "Demographic studies"]
    },
    {
      type: "Network & Relationship",
      description: "Visualizations showing connections and relationships",
      examples: ["Network Graphs", "Tree Diagrams", "Sankey Diagrams", "Chord Diagrams", "Force-directed Graphs"],
      useCases: ["Social networks", "Organizational structures", "Data flow", "Dependencies"]
    },
    {
      type: "Time Series & Temporal",
      description: "Time-based data visualizations",
      examples: ["Timeline Charts", "Gantt Charts", "Calendar Views", "Stream Graphs", "Animated Charts"],
      useCases: ["Project planning", "Historical trends", "Seasonal patterns", "Event tracking"]
    }
  ],
  
  process: [
    {
      step: "1",
      title: "Data Discovery & Requirements",
      description: "Understand data sources, user needs, and visualization objectives",
      details: ["Data source analysis", "User interviews", "Requirements gathering", "Success metrics definition"]
    },
    {
      step: "2", 
      title: "Data Preparation & Modeling",
      description: "Clean, transform, and structure data for optimal visualization",
      details: ["Data cleaning", "ETL processes", "Data modeling", "Performance optimization"]
    },
    {
      step: "3",
      title: "Design & Prototyping",
      description: "Create wireframes and prototypes for visualization interfaces",
      details: ["UI/UX design", "Information architecture", "Interactive prototypes", "User testing"]
    },
    {
      step: "4",
      title: "Development & Integration",
      description: "Build and integrate visualization solutions with existing systems",
      details: ["Frontend development", "Backend integration", "API connections", "Real-time data feeds"]
    },
    {
      step: "5",
      title: "Testing & Deployment",
      description: "Ensure quality, performance, and successful deployment",
      details: ["Quality assurance", "Performance testing", "User acceptance", "Production deployment"]
    },
    {
      step: "6",
      title: "Monitoring & Optimization",
      description: "Continuously monitor and improve visualization performance",
      details: ["Usage analytics", "Performance monitoring", "User feedback", "Iterative improvements"]
    }
  ],
  
  benefits: [
    "Accelerate decision-making with instant insights",
    "Improve data comprehension by 70%",
    "Reduce time-to-insight from hours to minutes", 
    "Enable self-service analytics for business users",
    "Identify trends and patterns invisible in raw data",
    "Enhance stakeholder engagement and buy-in",
    "Support data-driven culture transformation",
    "Increase operational efficiency through visual monitoring"
  ],
  
  challenges: [
    {
      challenge: "Data Quality & Consistency",
      solution: "Automated data validation and cleansing pipelines",
      description: "Ensure accurate and reliable visualizations through data quality management"
    },
    {
      challenge: "Performance & Scalability",
      solution: "Optimized queries, caching, and progressive loading",
      description: "Maintain fast performance even with large datasets and many users"
    },
    {
      challenge: "User Adoption & Training",
      solution: "Intuitive design and comprehensive training programs",
      description: "Ensure users can effectively utilize visualization tools and insights"
    },
    {
      challenge: "Real-time Data Integration",
      solution: "Streaming architectures and efficient data pipelines",
      description: "Enable real-time visualizations with minimal latency"
    }
  ]
};

export default function DataVisualizationPage() {
  const params = useParams();
  const currentLocale = params?.locale as string || 'en';
  
  // Function to localize text
  const localize = (text: string) => localizeSpelling(text, currentLocale);
  
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Section */}
      <header className={`py-20 md:py-28 text-white ${dataVisualizationData.headerGradient} relative overflow-hidden`}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, white 2px, transparent 2px),
                             radial-gradient(circle at 75% 75%, white 2px, transparent 2px),
                             radial-gradient(circle at 50% 50%, white 1px, transparent 1px)`,
            backgroundSize: '50px 50px, 60px 60px, 30px 30px'
          }}></div>
        </div>
        
        <div className="container mx-auto max-w-6xl px-4 relative z-10">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <Link href={`/${currentLocale}`} className="inline-flex items-center text-sm text-white/80 hover:text-white transition-colors duration-300 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <ArrowLeft size={18} className="mr-2" />
              {localize("Back to Homepage")}
            </Link>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
              <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
                {localize(dataVisualizationData.title)}
              </h1>
              <p className="text-xl text-emerald-100 mb-8 leading-relaxed">
                {localize(dataVisualizationData.subtitle)}
              </p>
              <div className="flex flex-wrap gap-3">
                {["Interactive", "Real-time", "Custom", "Insights"].map((tag, index) => (
                  <Badge key={tag} variant="secondary" className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                    {localize(tag)}
                  </Badge>
                ))}
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <BarChart3 className="w-12 h-12 mx-auto mb-3 text-emerald-200" />
                    <div className="text-2xl font-bold">70%</div>
                    <div className="text-sm text-emerald-100">{localize("Better Comprehension")}</div>
                  </div>
                  <div className="text-center">
                    <TrendingUp className="w-12 h-12 mx-auto mb-3 text-emerald-200" />
                    <div className="text-2xl font-bold">10x</div>
                    <div className="text-sm text-emerald-100">{localize("Faster Insights")}</div>
                  </div>
                  <div className="text-center">
                    <Eye className="w-12 h-12 mx-auto mb-3 text-emerald-200" />
                    <div className="text-2xl font-bold">Real-time</div>
                    <div className="text-sm text-emerald-100">{localize("Live Updates")}</div>
                  </div>
                  <div className="text-center">
                    <Users className="w-12 h-12 mx-auto mb-3 text-emerald-200" />
                    <div className="text-2xl font-bold">Self-service</div>
                    <div className="text-sm text-emerald-100">{localize("User Analytics")}</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto max-w-6xl px-4 py-16">
        
        {/* Overview Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-slate-200">
            <h2 className={`text-3xl font-bold mb-6 ${dataVisualizationData.accentColor}`}>
              {localize("What is Data Visualization?")}
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed">
              {localize(dataVisualizationData.overview)}
            </p>
          </div>
        </motion.section>

        {/* Core Capabilities */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Core Data Visualization Capabilities")}
          </motion.h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {dataVisualizationData.capabilities.map((capability, index) => (
              <motion.div key={capability.name} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300 border-l-4 border-l-emerald-500">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                        <capability.icon className="w-6 h-6 text-emerald-600" />
                      </div>
                      <CardTitle className="text-xl">{localize(capability.name)}</CardTitle>
                    </div>
                    <CardDescription className="text-base">
                      {localize(capability.description)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Applications")}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {capability.applications.map((app) => (
                            <Badge key={app} variant="outline" className="text-xs">
                              {localize(app)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Use Cases")}
                        </h4>
                        <ul className="space-y-1">
                          {capability.useCases.map((useCase) => (
                            <li key={useCase} className="text-sm text-slate-600 flex items-start">
                              <CheckCircle className="w-3 h-3 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                              {localize(useCase)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Visualization Types Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Types of Data Visualizations")}
          </motion.h2>
          
          <div className="space-y-8">
            {dataVisualizationData.visualizationTypes.map((type, index) => (
              <motion.div key={type.type} variants={itemVariants}>
                <Card className="hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-xl flex items-center gap-2">
                      <div className="w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {index + 1}
                      </div>
                      {localize(type.type)}
                    </CardTitle>
                    <CardDescription className="text-base">{localize(type.description)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-3">
                          {localize("Examples")}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {type.examples.map((example) => (
                            <Badge key={example} variant="secondary" className="text-xs">
                              {localize(example)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-3">
                          {localize("Common Use Cases")}
                        </h4>
                        <ul className="space-y-2">
                          {type.useCases.map((useCase) => (
                            <li key={useCase} className="text-sm text-slate-600 flex items-start">
                              <CheckCircle className="w-3 h-3 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                              {localize(useCase)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Technology Stack */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-12">
            {localize("Data Visualization Technology Stack")}
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dataVisualizationData.technologies.map((tech, index) => (
              <Card key={tech.category} className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Code className="w-5 h-5 text-emerald-600" />
                    {localize(tech.category)}
                  </CardTitle>
                  <CardDescription>{localize(tech.description)}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {tech.tools.map((tool) => (
                      <Badge key={tool} variant="secondary" className="text-xs">
                        {tool}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.section>

        {/* Data Visualization Challenges */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Data Visualization Challenges & Solutions")}
          </motion.h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {dataVisualizationData.challenges.map((item, index) => (
              <motion.div key={item.challenge} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg text-red-600">{localize(item.challenge)}</CardTitle>
                    <CardDescription className="text-base">{localize(item.description)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                      <h4 className="font-semibold text-emerald-800 mb-2">{localize("Our Solution")}</h4>
                      <p className="text-sm text-emerald-700">{localize(item.solution)}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Industry Applications */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mb-20"
        >
          <motion.h2 variants={itemVariants} className="text-3xl font-bold text-center mb-12">
            {localize("Industry Applications")}
          </motion.h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dataVisualizationData.industries.map((industry, index) => (
              <motion.div key={industry.name} variants={itemVariants}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <CardTitle className="text-lg">{localize(industry.name)}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Applications")}
                        </h4>
                        <ul className="space-y-1">
                          {industry.applications.map((app) => (
                            <li key={app} className="text-sm text-slate-600 flex items-start">
                              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-2 mt-2 flex-shrink-0"></div>
                              {localize(app)}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="pt-3 border-t border-slate-200">
                        <h4 className="font-semibold text-sm uppercase tracking-wider text-slate-600 mb-2">
                          {localize("Impact")}
                        </h4>
                        <p className="text-sm text-emerald-700 font-medium">{localize(industry.impact)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Our Process */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-center mb-12">
            {localize("Our Data Visualization Development Process")}
          </h2>
          
          <div className="space-y-8">
            {dataVisualizationData.process.map((step, index) => (
              <div key={step.step} className="flex gap-6 items-start">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold text-lg">
                    {step.step}
                  </div>
                </div>
                <div className="flex-1">
                  <Card className="hover:shadow-lg transition-shadow duration-300">
                    <CardHeader>
                      <CardTitle className="text-xl">{localize(step.title)}</CardTitle>
                      <CardDescription className="text-base">{localize(step.description)}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="grid md:grid-cols-2 gap-2">
                        {step.details.map((detail) => (
                          <li key={detail} className="text-sm text-slate-600 flex items-start">
                            <CheckCircle className="w-3 h-3 text-emerald-500 mr-2 mt-0.5 flex-shrink-0" />
                            {localize(detail)}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Benefits */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="mb-20"
        >
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 md:p-12 border border-emerald-200">
            <h2 className="text-3xl font-bold text-center mb-8">
              {localize("Why Choose Data Visualization?")}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {dataVisualizationData.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-lg shadow-sm">
                  <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-slate-700">{localize(benefit)}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section 
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          className="text-center"
        >
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-slate-200">
            <h2 className="text-3xl font-bold mb-4">
              {localize("Ready to Transform Your Data into Insights?")}
            </h2>
            <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
              {localize("Let our data visualization experts help you create compelling visual stories that drive decision-making and business growth.")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                href={`/${currentLocale}/contact`}
                className={`${dataVisualizationData.buttonColor} text-white font-semibold py-3 px-8 rounded-lg text-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105`}
              >
                {localize("Start Visualizing Data")}
              </Link>
              <Link 
                href={`/${currentLocale}/services`}
                className="bg-slate-100 text-slate-700 font-semibold py-3 px-8 rounded-lg text-lg hover:bg-slate-200 transition-colors duration-300"
              >
                {localize("Explore Our Services")}
              </Link>
            </div>
          </div>
        </motion.section>
      </main>
    </div>
  );
} 